import { identify, Identify, track } from './amplitude';
import type { QualityProfile } from '../scene/qualityProfile';
import { $locale } from '../state/locale';
import { LAYOUT_MODE_CHANGED } from './events';
import { LAYOUT_DESKTOP_BREAKPOINT } from '../hooks/useLayoutMode';

export type LayoutModeName = 'mobile' | 'desktop';

const readIsPreviewFrame = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get('viewMode') === 'mobile'
  );
};

const readLayoutMode = (): LayoutModeName => {
  if (typeof window === 'undefined') return 'mobile';
  // Mobile preview frame forces mobile layout regardless of window width —
  // mirrors `useLayoutMode` logic so analytics matches React-side
  // perception.
  if (readIsPreviewFrame()) return 'mobile';
  return window.innerWidth >= LAYOUT_DESKTOP_BREAKPOINT ? 'desktop' : 'mobile';
};

const setLayoutUserProperties = (properties: InstanceType<typeof Identify>): void => {
  properties.set('layout_mode', readLayoutMode());
  properties.set('viewport_width', typeof window === 'undefined' ? 0 : window.innerWidth);
  properties.set('is_preview_frame', readIsPreviewFrame());
};

export const identifyDevice = (profile: QualityProfile) => {
  const userProperties = new Identify();
  userProperties.set('device_tier', profile.tier);
  userProperties.set('device_class', profile.deviceClass);
  if (profile.benchmarkMs != null) {
    userProperties.set('gpu_benchmark_ms', profile.benchmarkMs);
  }
  if (profile.benchmarkCv != null) {
    userProperties.set('gpu_benchmark_cv', profile.benchmarkCv);
  }
  userProperties.set('locale', $locale.getState());
  setLayoutUserProperties(userProperties);
  identify(userProperties);
};

/** Refresh layout user properties — call when layoutMode flips at runtime
 *  (cross-breakpoint resize). Keeps segmentation accurate without
 *  re-running the GPU benchmark or the full device identify pipeline. */
export const identifyLayoutMode = () => {
  const userProperties = new Identify();
  setLayoutUserProperties(userProperties);
  identify(userProperties);
};

/** Emit a discrete event for cross-breakpoint resize. Rare in practice
 *  (most users don't resize between mobile/desktop widths mid-session),
 *  but the data point is useful for understanding adaptive-layout impact. */
export const layoutModeChanged = (from: LayoutModeName, to: LayoutModeName): void => {
  track(LAYOUT_MODE_CHANGED, {
    from,
    to,
    viewport_width: typeof window === 'undefined' ? 0 : window.innerWidth,
  });
};
